import { useQuery } from '@tanstack/react-query';
import type {
  NlWkrCategory,
  NlWkrEntryIndexRowWire,
  NlWkrSourceCategoriesResponse,
  NlWkrSourceType,
  NlWkrTotalsResponse,
} from '@releve/shared';
import { authedGet } from '@/lib/api';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import { useFeatureEnabled } from '@/hooks/useOrganizationFeatures';
import { useJurisdictionTaxConfig } from '@/hooks/useJurisdictionTaxConfig';
import { useFullTaxSettings } from '@/hooks/useOrganizationTaxSettings';
import { useOrganization } from '@/contexts/OrganizationContext';

/**
 * NL WKR (Werkkostenregeling) hooks — the read side of the nl_wkr subsystem
 * plus the double gate every WKR surface hangs off.
 *
 * Liveness contract: every hook keys under the `['nl-wkr', orgId]` prefix,
 * invalidated by the mutation hooks of all four booking surfaces
 * (useExpenses, useAccountsPayable, useLedgerTransactions,
 * useAddExpenseFromImport, useClearingActions) — bridge rows are written as a
 * side effect of those document writes, never by a WKR mutation of its own.
 */

/**
 * The WKR gate: feature flag `nl_wkr` AND tax jurisdiction 'NL'.
 * The jurisdiction comes from useJurisdictionTaxConfig, whose precedence is the
 * shared `resolveTaxJurisdictionFrom` — the same helper the api's
 * assertWkrAccess (apps/api/src/routes/accounting/nl/wkr.ts, via
 * lib/orgJurisdiction.ts) resolves through, so there is one rule, not a copy.
 *
 * Hold gated UI out of the tree while `isLoading` (tab pop-in rule,
 * PlaceDetails precedent) — WkrCategorySelect self-gates on this hook.
 */
export function useNlWkrEnabled(): { enabled: boolean; isLoading: boolean } {
  const { selectedOrganization } = useOrganization();
  const flag = useFeatureEnabled('nl_wkr');
  const taxConfig = useJurisdictionTaxConfig();
  // Review fix: `isLoading` must cover the ASYNC jurisdiction source too —
  // organization_tax_settings is a fetch, and until it lands an org whose
  // country fallback isn't 'NL' reads as gate-off with isLoading=false,
  // letting edit-form hydration run against a disabled source query (which a
  // later save turns into a silent classification wipe).
  const { isLoading: taxSettingsLoading } = useFullTaxSettings(selectedOrganization?.id);
  return {
    enabled: flag.enabled && taxConfig.jurisdiction === 'NL',
    isLoading: flag.isLoading || taxSettingsLoading,
  };
}

/**
 * Per-category totals for an INCLUSIVE [from, to] date range (signed; voided
 * AP excluded). The range comes from the app-standard DateRangeFilter.
 */
export function useNlWkrTotals(
  organizationId: string | undefined,
  from: string | undefined,
  to: string | undefined,
) {
  const { enabled } = useNlWkrEnabled();
  return useQuery({
    queryKey: ['nl-wkr', organizationId, 'totals', from ?? null, to ?? null],
    queryFn: () =>
      authedGet<NlWkrTotalsResponse>(
        `/api/v1/nl/wkr/orgs/${organizationId}/totals?from=${from}&to=${to}`,
      ),
    enabled: enabled && !!organizationId && !!from && !!to,
    staleTime: 60_000,
  });
}

/**
 * The COMPLETE drilldown index for an inclusive date range — keyset-paged
 * server-side, so never silently truncated; also the CSV export's data source.
 */
export function useNlWkrEntryIndex(
  organizationId: string | undefined,
  from: string | undefined,
  to: string | undefined,
) {
  const { enabled } = useNlWkrEnabled();
  return useQuery({
    ...indexQueryOptions<NlWkrEntryIndexRowWire>({
      entity: 'nl-wkr',
      organizationId,
      path: `/api/v1/nl/wkr/orgs/${organizationId}/entries/index`,
      params: { from: from ?? null, to: to ?? null },
    }),
    enabled: enabled && !!organizationId && !!from && !!to,
  });
}

// NOTE: the unreviewed-lines queue is API-only for now — the report's callout
// was removed per product decision (2026-08-10); GET .../unreviewed and its
// RPC remain live and tested for future use.

/**
 * Existing classifications for one source document, keyed by source_line_id
 * (invoice_line_items.id / ledger_entries.id) — edit-form hydration. Returns
 * a lookup map; merge into form line state by the LOADED line/entry id, never
 * by array position (ledger entries have no recoverable ordinal).
 */
export function useNlWkrSourceCategories(
  organizationId: string | undefined,
  sourceType: NlWkrSourceType,
  sourceId: string | undefined,
): { data: Record<string, NlWkrCategory> | undefined; isLoading: boolean } {
  const gate = useNlWkrEnabled();
  const active = gate.enabled && !!organizationId && !!sourceId;
  const query = useQuery({
    queryKey: ['nl-wkr', organizationId, 'source', sourceType, sourceId ?? null],
    queryFn: async (): Promise<Record<string, NlWkrCategory>> => {
      const result = await authedGet<NlWkrSourceCategoriesResponse>(
        `/api/v1/nl/wkr/orgs/${organizationId}/source/${sourceType}/${sourceId}`,
      );
      return Object.fromEntries(result.items.map((i) => [i.source_line_id, i.category]));
    },
    enabled: active,
    staleTime: 30_000,
  });
  return {
    data: query.data,
    // Review fix — composite READINESS, not TanStack's isLoading (a disabled
    // or errored v5 query reports isLoading=false with data undefined, which
    // let hydration seed '' everywhere and a later save DELETE the document's
    // classifications). Hydration may proceed only when the gate has settled
    // AND, for a gated edit, the fetch has SUCCEEDED. An errored fetch stays
    // "loading" on purpose: blocking the edit form while the WKR endpoint is
    // down is the honest trade against a silent data wipe; default retries
    // absorb transient blips. Create mode (no sourceId) never blocks.
    isLoading: gate.isLoading || (active && !query.isSuccess),
  };
}
